import { useQuery, useMutation } from '@apollo/client/react/index.js';
import {
  AssigneePills,
  cn,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassInput,
  GlassSelect,
  GlassTabPanel,
  GlassTabs,
  normalizeServiceOrderStatus,
  SERVICE_ORDER_EDIT_OPTIONS,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SkeletonLoader,
} from '@merritrac/shared/components';
import {
  ArrowLeft,
  Building2,
  Calendar,
  ChevronRight,
  ClipboardList,
  Clock,
  Edit2,
  Layers,
  MapPin,
  Save,
  Search,
  Timer,
  Users,
  Wrench,
  Trash2,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { ActivityTimeline } from '../components/ActivityTimeline.js';
import { ServiceOrderMap } from '../components/ServiceOrderMap.js';
import { ServiceOrderPerformance } from '../components/ServiceOrderPerformance.js';
import {
  CREATE_SERVICE_ORDER,
  DELETE_SERVICE_ORDER,
  GET_SERVICE_ORDER,
  GET_SERVICE_ORDERS,
  GET_UNIT_LOCATIONS,
  GET_UNIT_SUMMARIES,
  UPDATE_SERVICE_ORDER,
} from '../lib/graphql/operations.js';
import { useHeaderConfig } from '../lib/headerConfig.js';

import type { ServiceOrderStatus } from '@merritrac/shared/components';
import type { ReactElement } from 'react';

/* ── Types ── */

type Priority = 'low' | 'medium' | 'high' | 'critical';

interface ApiClient {
  clientCode: string;
  clientName: string | null;
  contact: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
}

interface ApiServiceOrder {
  accessKey: number;
  linkedAk: number | null;
  ticketno: string | null;
  description: string | null;
  notes: string | null;
  status: string | null;
  priority: string | null;
  assignedTo: string | null;
  clientCode: string | null;
  createdDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  jobSite: string | null;
  jobType: string | null;
  manpower: number | null;
  duration: number | null;
  defaultCrew: string | null;
  scheduled: number | null;
  client: ApiClient | null;
  checklistTemplate: {
    id: number;
    name: string;
    jobType: string | null;
  } | null;
}

interface GetServiceOrderData {
  serviceOrder: ApiServiceOrder | null;
}

interface ServiceOrderDetail {
  id: string;
  orderNumber: string;
  client: string;
  location: string;
  status: ServiceOrderStatus;
  priority: Priority;
  assignee: string;
  serviceType: string;
  description: string;
  createdDate: string;
  scheduledDate: string;
  completedDate: string | null;
}

/* ── Zod validation schema ── */

const serviceOrderSchema = z.object({
  client: z.string().min(1, 'Client is required'),
  location: z.string().optional(),
  status: z.enum(['pending', 'in-progress', 'completed', 'cancelled']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  assignee: z.string().optional(),
  serviceType: z.string().optional(),
  description: z.string().optional(),
  scheduledDate: z.string().optional(),
});

/* ── Lookup maps ── */

const priorityVariant: Record<Priority, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

const priorityLabel: Record<Priority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const priorityToInt: Record<Priority, number> = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

const priorityOptions: readonly { value: string; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

type TabId = 'details' | 'units' | 'performance';

/* ── Helpers ── */

function normalizePriority(raw: string | null | undefined): Priority {
  if (raw === null || raw === undefined || raw === '') {
    return 'medium';
  }
  const p = raw.toLowerCase();
  if (p === 'critical') {
    return 'critical';
  }
  if (p === 'high') {
    return 'high';
  }
  if (p === 'low') {
    return 'low';
  }
  return 'medium';
}

function formatDate(raw: string | null): string {
  if (raw === null || raw === '') {
    return '\u2014';
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) {
    return raw;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function apiNodeToDetail(node: ApiServiceOrder): ServiceOrderDetail {
  const city = node.client?.city ?? '';
  const state = node.client?.state ?? '';
  const cityState = [city, state].filter((s) => s !== '').join(', ');
  const location = node.jobSite ?? (cityState !== '' ? cityState : '\u2014');

  return {
    id: String(node.accessKey),
    orderNumber: node.ticketno ?? String(node.accessKey),
    client: node.client?.clientName ?? '\u2014',
    location,
    status: normalizeServiceOrderStatus(node.status),
    priority: normalizePriority(node.priority),
    assignee: node.assignedTo ?? '\u2014',
    serviceType: node.description ?? '',
    description: node.notes ?? '',
    createdDate: node.createdDate ?? '',
    scheduledDate: node.dueDate ?? node.createdDate ?? '',
    completedDate: node.completedDate ?? null,
  };
}

function buildFullAddress(client: ApiClient | null): string {
  if (client === null) {
    return '';
  }
  const parts = [client.address, client.city, client.state, client.zip].filter(
    (s): s is string => s !== null && s !== '',
  );
  return parts.join(', ');
}

/* ── Status Widget ── */

function StatusWidget({ serviceOrderAccessKey }: { serviceOrderAccessKey: number }): ReactElement {
  const { data, loading } = useQuery<UnitSummariesData>(GET_UNIT_SUMMARIES, {
    variables: { serviceOrderAccessKey },
  });

  const summaries = data?.unitSummaries ?? [];

  // Sort: partial progress first, then pending, then complete
  const sorted = [...summaries].sort((a, b): number => {
    function rank(s: UnitSummary): number {
      if (s.locationCount === 0 || s.completedCount === 0) {
        return 1;
      } // pending
      if (s.completedCount < s.locationCount) {
        return 0;
      } // partial first
      return 2; // complete last
    }
    return rank(a) - rank(b);
  });

  const display = sorted.slice(0, 8);

  return (
    <GlassCard>
      <GlassCardHeader>
        <h2 className="text-sm font-semibold text-text">Unit Status</h2>
      </GlassCardHeader>
      <GlassCardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonLoader key={i} variant="block" height="28px" />
            ))}
          </div>
        ) : display.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-6">No units tracked yet.</p>
        ) : (
          <div className="space-y-2">
            {display.map((unit) => {
              const pct =
                unit.locationCount > 0
                  ? Math.round((unit.completedCount / unit.locationCount) * 100)
                  : 0;
              return (
                <div key={`${unit.floor}-${unit.unit}`} className="flex items-center gap-3">
                  <span className="font-mono text-xs font-semibold text-primary w-12 shrink-0 truncate">
                    {unit.unit}
                  </span>
                  <span className="text-[11px] text-text-tertiary w-10 shrink-0">
                    F{unit.floor}
                  </span>
                  <div className="flex-1">
                    <UnitProgress pct={pct} />
                  </div>
                  <span className="text-xs text-text-secondary tabular-nums w-10 text-right shrink-0">
                    {String(unit.completedCount)}/{String(unit.locationCount)}
                  </span>
                </div>
              );
            })}
            {sorted.length > 8 && (
              <p className="text-[11px] text-text-tertiary text-center pt-1">
                +{String(sorted.length - 8)} more units
              </p>
            )}
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Field display ── */

interface DetailFieldProps {
  label: string;
  value: string;
  icon?: ReactElement;
}

function DetailField({ label, value, icon }: DetailFieldProps): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider">{label}</p>
      <div className="flex items-center gap-1.5 text-sm text-text">
        {icon !== undefined && <span className="text-text-tertiary shrink-0">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}

/* ── Progress bar ── */

function UnitProgress({ pct }: { pct: number }): ReactElement {
  const clamp = Math.min(100, Math.max(0, pct));
  const barColor = clamp === 100 ? 'bg-success-fg' : clamp > 0 ? 'bg-primary' : 'bg-border-strong';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 rounded-full bg-bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${String(clamp)}%` }}
        />
      </div>
      <span className="text-xs text-text-secondary tabular-nums w-8 text-right">
        {String(clamp)}%
      </span>
    </div>
  );
}

/* ── Paginated Units Tab with Accordion ── */

/* ── Types for efficient unit queries ── */

interface UnitSummary {
  floor: string;
  unit: string;
  locationCount: number;
  completedCount: number;
  deniedCount: number;
}

interface UnitSummariesData {
  unitSummaries: UnitSummary[];
}

interface UnitLocationDetail {
  accessKey: number;
  location: string | null;
  serviceComplete: boolean | null;
  accessDenied: boolean | null;
  servicetech: string | null;
  comments: string | null;
}

interface UnitLocationsData {
  unitLocations: UnitLocationDetail[];
}

interface FloorGroup {
  floor: string;
  units: UnitSummary[];
  totalLocations: number;
  completedLocations: number;
  deniedLocations: number;
}

function groupSummariesByFloor(summaries: UnitSummary[]): FloorGroup[] {
  const map = new Map<string, UnitSummary[]>();
  for (const s of summaries) {
    let arr = map.get(s.floor);
    if (arr === undefined) {
      arr = [];
      map.set(s.floor, arr);
    }
    arr.push(s);
  }
  const floors: FloorGroup[] = [];
  for (const [floor, units] of map) {
    floors.push({
      floor,
      units,
      totalLocations: units.reduce((s, u) => s + u.locationCount, 0),
      completedLocations: units.reduce((s, u) => s + u.completedCount, 0),
      deniedLocations: units.reduce((s, u) => s + u.deniedCount, 0),
    });
  }
  return floors; // Already sorted by the SQL query
}

function UnitLocations({
  serviceOrderAccessKey,
  unit,
  floor,
}: {
  serviceOrderAccessKey: number;
  unit: string;
  floor: string;
}): ReactElement {
  const { data, loading } = useQuery<UnitLocationsData>(GET_UNIT_LOCATIONS, {
    variables: { serviceOrderAccessKey, unit, floor },
  });

  if (loading) {
    return <div className="pl-20 pr-5 py-3 text-xs text-text-tertiary">Loading locations…</div>;
  }

  const locations = data?.unitLocations ?? [];

  return (
    <div className="bg-bg-muted/30">
      {locations.map((loc) => (
        <div
          key={loc.accessKey}
          className="pl-20 pr-5 py-1.5 flex items-center gap-3 text-xs border-t border-border-subtle/30"
        >
          <span className="text-text-secondary flex-1">{loc.location ?? '\u2014'}</span>
          <span className="text-text-tertiary w-24 truncate">{loc.servicetech ?? '\u2014'}</span>
          {loc.accessDenied === true ? (
            <span className="text-danger font-medium w-14 text-right">Denied</span>
          ) : loc.serviceComplete === true ? (
            <span className="text-success font-medium w-14 text-right">Done</span>
          ) : (
            <span className="text-text-tertiary w-14 text-right">Pending</span>
          )}
        </div>
      ))}
    </div>
  );
}

function UnitsTab({ serviceOrderAccessKey }: { serviceOrderAccessKey: number }): ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set());
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());

  const { data, loading } = useQuery<UnitSummariesData>(GET_UNIT_SUMMARIES, {
    variables: { serviceOrderAccessKey },
  });

  const summaries = data?.unitSummaries ?? [];
  const floors = groupSummariesByFloor(summaries);

  const filtered =
    searchTerm === ''
      ? floors
      : floors
          .map((f) => ({
            ...f,
            units: f.units.filter((u) => u.unit.toLowerCase().includes(searchTerm.toLowerCase())),
          }))
          .filter((f) => f.units.length > 0);

  const totalUnits = summaries.length;
  const totalLocations = summaries.reduce((s, u) => s + u.locationCount, 0);

  function toggleFloor(floor: string): void {
    setExpandedFloors((prev) => {
      const next = new Set(prev);
      if (next.has(floor)) {
        next.delete(floor);
      } else {
        next.add(floor);
      }
      return next;
    });
  }

  function toggleUnit(key: string): void {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <GlassTabPanel className="pt-0">
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-sm font-semibold text-text shrink-0">
              Units
              <span className="ml-2 text-xs font-normal text-text-secondary">
                ({String(floors.length)} floors, {String(totalUnits)} units,{' '}
                {String(totalLocations)} locations)
              </span>
            </h2>
            <div className="relative max-w-xs w-full">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none z-10"
              />
              <GlassInput
                placeholder="Search units…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-8 w-full"
                aria-label="Search units"
              />
            </div>
          </div>
        </GlassCardHeader>

        {loading ? (
          <GlassCardContent>
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonLoader key={i} variant="block" height="40px" />
              ))}
            </div>
          </GlassCardContent>
        ) : filtered.length === 0 ? (
          <GlassCardContent>
            <p className="text-sm text-text-secondary text-center py-8">
              {searchTerm !== ''
                ? 'No units match your search.'
                : 'No units associated with this order.'}
            </p>
          </GlassCardContent>
        ) : (
          <div className="divide-y divide-border-subtle">
            {filtered.map((floor) => {
              const floorExpanded = expandedFloors.has(floor.floor);
              const floorPct =
                floor.totalLocations > 0
                  ? Math.round((floor.completedLocations / floor.totalLocations) * 100)
                  : 0;

              return (
                <div key={floor.floor}>
                  {/* Floor row */}
                  <button
                    type="button"
                    onClick={() => {
                      toggleFloor(floor.floor);
                    }}
                    className="w-full px-5 py-3 flex items-center gap-4 hover:bg-surface-raised/50 transition-colors cursor-pointer text-left"
                  >
                    <ChevronRight
                      size={14}
                      className={cn(
                        'text-text-tertiary shrink-0 transition-transform',
                        floorExpanded && 'rotate-90',
                      )}
                    />
                    <span className="text-sm font-semibold text-text w-20 shrink-0">
                      Floor {floor.floor}
                    </span>
                    <span className="text-xs text-text-secondary flex-1">
                      {String(floor.units.length)} units
                    </span>
                    <span className="text-xs text-text-secondary shrink-0">
                      {String(floor.completedLocations)}/{String(floor.totalLocations)}
                    </span>
                    <div className="w-24 shrink-0">
                      <UnitProgress pct={floorPct} />
                    </div>
                    {floor.deniedLocations > 0 ? (
                      <GlassBadge variant="danger">Denied</GlassBadge>
                    ) : floorPct === 100 ? (
                      <GlassBadge variant="success">Done</GlassBadge>
                    ) : floorPct > 0 ? (
                      <GlassBadge variant="info">Partial</GlassBadge>
                    ) : (
                      <GlassBadge variant="warning">Pending</GlassBadge>
                    )}
                  </button>

                  {/* Expanded: units on this floor */}
                  {floorExpanded && (
                    <div className="border-t border-border-subtle">
                      {floor.units.map((unit) => {
                        const unitKey = `${floor.floor}-${unit.unit}`;
                        const unitExpanded = expandedUnits.has(unitKey);
                        const unitPct =
                          unit.locationCount > 0
                            ? Math.round((unit.completedCount / unit.locationCount) * 100)
                            : 0;

                        return (
                          <div
                            key={unit.unit}
                            className="border-b border-border-subtle/50 last:border-b-0"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                toggleUnit(unitKey);
                              }}
                              className="w-full pl-10 pr-5 py-2.5 flex items-center gap-4 hover:bg-surface-raised/30 transition-colors cursor-pointer text-left"
                            >
                              <ChevronRight
                                size={12}
                                className={cn(
                                  'text-text-tertiary shrink-0 transition-transform',
                                  unitExpanded && 'rotate-90',
                                )}
                              />
                              <span className="font-mono text-xs font-semibold text-primary w-14 shrink-0">
                                {unit.unit}
                              </span>
                              <span className="text-xs text-text-secondary flex-1">
                                {String(unit.locationCount)} locations
                              </span>
                              <span className="text-xs text-text-secondary shrink-0">
                                {String(unit.completedCount)}/{String(unit.locationCount)}
                              </span>
                              <div className="w-20 shrink-0">
                                <UnitProgress pct={unitPct} />
                              </div>
                              {unit.deniedCount > 0 ? (
                                <span className="text-[10px] font-medium text-danger">Denied</span>
                              ) : unitPct === 100 ? (
                                <span className="text-[10px] font-medium text-success">Done</span>
                              ) : unitPct > 0 ? (
                                <span className="text-[10px] font-medium text-info-fg">
                                  Partial
                                </span>
                              ) : (
                                <span className="text-[10px] font-medium text-text-tertiary">
                                  Pending
                                </span>
                              )}
                            </button>

                            {/* Locations loaded on demand */}
                            {unitExpanded && (
                              <UnitLocations
                                serviceOrderAccessKey={serviceOrderAccessKey}
                                unit={unit.unit}
                                floor={unit.floor}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>
    </GlassTabPanel>
  );
}

/* ── Not Found ── */

function NotFound({ id, onBack }: { id: string; onBack: () => void }): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <ClipboardList size={48} className="text-text-tertiary" />
      <div className="text-center">
        <p className="text-lg font-semibold text-text">Order not found</p>
        <p className="text-sm text-text-secondary mt-1">
          No service order with ID &ldquo;{id}&rdquo; could be located.
        </p>
      </div>
      <GlassButton variant="secondary" size="md" onClick={onBack}>
        <ArrowLeft size={14} />
        Back to Service Orders
      </GlassButton>
    </div>
  );
}

/* ── Edit form state ── */

interface EditFields {
  client: string;
  location: string;
  status: ServiceOrderStatus;
  priority: Priority;
  assignee: string;
  serviceType: string;
  description: string;
  scheduledDate: string;
}

const emptyEditFields: EditFields = {
  client: '',
  location: '',
  status: 'pending',
  priority: 'medium',
  assignee: '',
  serviceType: '',
  description: '',
  scheduledDate: '',
};

function orderToEditFields(order: ServiceOrderDetail): EditFields {
  return {
    client: order.client,
    location: order.location,
    status: order.status,
    priority: order.priority,
    assignee: order.assignee,
    serviceType: order.serviceType,
    description: order.description,
    scheduledDate: order.scheduledDate,
  };
}

/* ── Page ── */

export function ServiceOrderDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isNew = id === 'new';
  const accessKey = isNew ? 0 : parseFloat(id ?? '0');

  const { data, loading, error } = useQuery<GetServiceOrderData>(GET_SERVICE_ORDER, {
    variables: { accessKey },
    skip: isNew || isNaN(accessKey) || accessKey === 0,
  });

  const [createServiceOrder] = useMutation(CREATE_SERVICE_ORDER);
  const [updateServiceOrder, { loading: saving }] = useMutation(UPDATE_SERVICE_ORDER, {
    refetchQueries: [{ query: GET_SERVICE_ORDER, variables: { accessKey } }],
  });
  const [deleteServiceOrder, { loading: deleting }] = useMutation(DELETE_SERVICE_ORDER, {
    refetchQueries: [{ query: GET_SERVICE_ORDERS, variables: { first: 50 } }],
  });
  const [showDelete, setShowDelete] = useState(false);

  const order: ServiceOrderDetail | undefined =
    data?.serviceOrder !== null && data?.serviceOrder !== undefined
      ? apiNodeToDetail(data.serviceOrder)
      : undefined;

  const [isEditing, setIsEditing] = useState(isNew);
  const [editFields, setEditFields] = useState<EditFields>(emptyEditFields);
  const [activeTab, setActiveTab] = useState<TabId>('details');

  const { setHeader, reset: resetHeader } = useHeaderConfig();

  // Build a descriptive title: "MTCC #1382 - FCM" (client - jobType)
  const jobType = data?.serviceOrder?.jobType ?? '';
  const pageTitle =
    order !== undefined
      ? [order.client, jobType].filter((s) => s !== '' && s !== '\u2014').join(' \u2013 ')
      : isNew
        ? 'New Service Order'
        : '';

  useEffect(() => {
    if (pageTitle !== '') {
      setHeader(pageTitle, '/service-orders');
    }
    return (): void => {
      resetHeader();
    };
  }, [pageTitle]);

  useEffect(() => {
    if (order !== undefined && !isEditing) {
      setEditFields(orderToEditFields(order));
    }
  }, [data]);

  function handleBack(): void {
    void navigate('/service-orders');
  }

  function handleEditToggle(): void {
    if (order !== undefined) {
      setEditFields(orderToEditFields(order));
    }
    setIsEditing(true);
  }

  function handleCancel(): void {
    if (order !== undefined) {
      setEditFields(orderToEditFields(order));
    }
    setIsEditing(false);
  }

  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  function handleSave(): void {
    const result = serviceOrderSchema.safeParse(editFields);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path[0] as string | undefined;
        if (path !== undefined) {
          errors[path] = issue.message;
        }
      }
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setSaveError(null);

    if (isNew) {
      void createServiceOrder({
        variables: {
          clientCode: editFields.client !== '' ? editFields.client : undefined,
          status: editFields.status,
          priority: priorityToInt[editFields.priority],
          assignedTo: editFields.assignee !== '' ? editFields.assignee : undefined,
          description: editFields.serviceType !== '' ? editFields.serviceType : undefined,
          notes: editFields.description !== '' ? editFields.description : undefined,
          dueDate: editFields.scheduledDate !== '' ? editFields.scheduledDate : undefined,
        },
      })
        .then((res) => {
          const d = res.data as { createServiceOrder?: { accessKey: number } } | undefined;
          const newAk = d?.createServiceOrder?.accessKey;
          if (newAk !== undefined) {
            void navigate(`/service-orders/${String(newAk)}`);
          }
        })
        .catch((err: unknown) => {
          setSaveError(err instanceof Error ? err.message : 'Failed to create service order');
        });
    } else {
      void updateServiceOrder({
        variables: {
          accessKey,
          status: editFields.status,
          priority: editFields.priority,
          assignedTo: editFields.assignee !== '' ? editFields.assignee : undefined,
          description: editFields.serviceType !== '' ? editFields.serviceType : undefined,
          notes: editFields.description !== '' ? editFields.description : undefined,
          dueDate: editFields.scheduledDate !== '' ? editFields.scheduledDate : undefined,
        },
      })
        .then((): void => {
          setIsEditing(false);
        })
        .catch((err: unknown): void => {
          setSaveError(err instanceof Error ? err.message : 'Failed to update service order');
        });
    }
  }

  function handleFieldChange(field: keyof EditFields, value: string): void {
    setEditFields((prev): EditFields => ({ ...prev, [field]: value }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  if (error !== undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-sm text-danger">Failed to load service order: {error.message}</p>
        <GlassButton variant="secondary" size="md" onClick={handleBack}>
          <ArrowLeft size={14} />
          Back to Service Orders
        </GlassButton>
      </div>
    );
  }

  if (order === undefined && !isNew) {
    return <NotFound id={id ?? ''} onBack={handleBack} />;
  }

  const displayOrder: ServiceOrderDetail = order ?? {
    id: '',
    orderNumber: 'NEW',
    client: editFields.client,
    location: editFields.location,
    status: editFields.status,
    priority: editFields.priority,
    assignee: editFields.assignee,
    serviceType: editFields.serviceType,
    description: editFields.description,
    createdDate: new Date().toISOString().split('T')[0] ?? '',
    scheduledDate: editFields.scheduledDate,
    completedDate: null,
  };

  const tabDefs = [
    { id: 'details', label: 'Details' },
    { id: 'units', label: 'Units' },
    { id: 'performance', label: 'Performance' },
  ] as const;

  return (
    <div className="space-y-5">
      {/* Order header card (with inline actions) */}
      <GlassCard>
        <GlassCardContent className="py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <GlassBadge variant={SERVICE_ORDER_STATUS_VARIANT[displayOrder.status]}>
                  {SERVICE_ORDER_STATUS_LABEL[displayOrder.status]}
                </GlassBadge>
                <GlassBadge variant={priorityVariant[displayOrder.priority]}>
                  {priorityLabel[displayOrder.priority]}
                </GlassBadge>
                {jobType !== '' && <GlassBadge variant="info">{jobType}</GlassBadge>}
              </div>
              <h1 className="text-lg font-semibold text-text">{displayOrder.client}</h1>
              <div className="flex items-center gap-1.5 text-sm text-text-secondary mt-0.5">
                <MapPin size={13} className="shrink-0" />
                <span>{displayOrder.location}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:items-end shrink-0">
              {/* Action buttons */}
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      <X size={14} />
                      Cancel
                    </GlassButton>
                    <GlassButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                      <Save size={14} />
                      {saving ? 'Saving…' : 'Save'}
                    </GlassButton>
                  </>
                ) : (
                  <>
                    <GlassButton
                      variant="ghost"
                      size="sm"
                      className="text-danger hover:text-danger hover:bg-danger-soft"
                      onClick={() => {
                        setShowDelete(true);
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </GlassButton>
                    <GlassButton variant="secondary" size="sm" onClick={handleEditToggle}>
                      <Edit2 size={14} />
                      Edit
                    </GlassButton>
                  </>
                )}
              </div>
              {/* Dates */}
              <div className="flex flex-col gap-1 sm:items-end text-sm text-text-secondary">
                <div className="flex items-center gap-1.5">
                  <Calendar size={13} className="shrink-0" />
                  <span>Scheduled: {formatDate(displayOrder.scheduledDate)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="shrink-0" />
                  <span>Created: {formatDate(displayOrder.createdDate)}</span>
                </div>
                {displayOrder.completedDate !== null && (
                  <div className="flex items-center gap-1.5">
                    <Clock size={13} className="shrink-0" />
                    <span>Completed: {formatDate(displayOrder.completedDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Tabs */}
      <GlassTabs
        tabs={tabDefs}
        activeTab={activeTab}
        onTabChange={(tid): void => {
          setActiveTab(tid as TabId);
        }}
      />

      {/* Details + Activity tab */}
      {activeTab === 'details' && (
        <GlassTabPanel className="pt-0">
          <div className="space-y-5">
            {/* Row 1: Map + Order Details */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Map */}
              <div className="lg:col-span-2">
                <ServiceOrderMap address={buildFullAddress(data?.serviceOrder?.client ?? null)} />
              </div>

              {/* Order details card */}
              <div className="lg:col-span-3">
                <GlassCard className="h-full">
                  <GlassCardHeader>
                    <h2 className="text-sm font-semibold text-text">Order Details</h2>
                  </GlassCardHeader>
                  <GlassCardContent>
                    {isEditing ? (
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <GlassInput
                          label="Client"
                          value={editFields.client}
                          error={validationErrors['client']}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            setValidationErrors((prev) => {
                              const next = { ...prev };
                              delete next['client'];
                              return next;
                            });
                            handleFieldChange('client', e.target.value);
                          }}
                        />
                        <GlassInput
                          label="Location"
                          value={editFields.location}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            handleFieldChange('location', e.target.value);
                          }}
                        />
                        <GlassSelect
                          label="Status"
                          options={SERVICE_ORDER_EDIT_OPTIONS}
                          value={editFields.status}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                            handleFieldChange('status', e.target.value);
                          }}
                        />
                        <GlassSelect
                          label="Priority"
                          options={priorityOptions}
                          value={editFields.priority}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                            handleFieldChange('priority', e.target.value);
                          }}
                        />
                        <GlassInput
                          label="Assigned Technician"
                          value={editFields.assignee}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            handleFieldChange('assignee', e.target.value);
                          }}
                        />
                        <GlassInput
                          label="Scheduled Date"
                          type="date"
                          value={editFields.scheduledDate}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            handleFieldChange('scheduledDate', e.target.value);
                          }}
                        />
                        <div className="sm:col-span-2 flex flex-col gap-1.5">
                          <label
                            htmlFor="description"
                            className="text-sm font-medium text-text-secondary"
                          >
                            Description
                          </label>
                          <textarea
                            id="description"
                            rows={3}
                            value={editFields.description}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
                              handleFieldChange('description', e.target.value);
                            }}
                            className={cn(
                              'px-3 py-2 rounded-md border border-border bg-bg-elevated text-text text-sm',
                              'placeholder:text-text-tertiary resize-y',
                              'transition-colors duration-[var(--dur)] ease-[var(--ease)]',
                              'hover:border-border-strong',
                              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
                            )}
                          />
                        </div>
                        {saveError !== null && (
                          <div className="sm:col-span-2 p-3 rounded-md bg-danger-soft text-danger text-sm">
                            {saveError}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <DetailField
                            label="Address"
                            value={
                              buildFullAddress(data?.serviceOrder?.client ?? null) ||
                              displayOrder.location
                            }
                            icon={<MapPin size={13} />}
                          />
                          <DetailField
                            label="Priority"
                            value={priorityLabel[displayOrder.priority]}
                            icon={<ClipboardList size={13} />}
                          />
                          <DetailField
                            label="Scheduled"
                            value={formatDate(displayOrder.scheduledDate)}
                            icon={<Calendar size={13} />}
                          />
                          <div className="flex flex-col gap-0.5">
                            <p className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider">
                              Assigned
                            </p>
                            <div className="flex items-center gap-1.5">
                              <span className="text-text-tertiary shrink-0">
                                <Users size={13} />
                              </span>
                              <AssigneePills names={displayOrder.assignee} />
                            </div>
                          </div>
                          <DetailField
                            label="Client"
                            value={displayOrder.client}
                            icon={<Building2 size={13} />}
                          />
                          {displayOrder.serviceType !== '' && (
                            <DetailField
                              label="Service Type"
                              value={displayOrder.serviceType}
                              icon={<Wrench size={13} />}
                            />
                          )}
                          {data?.serviceOrder?.manpower !== null &&
                            data?.serviceOrder?.manpower !== undefined && (
                              <DetailField
                                label="Manpower"
                                value={`${String(data.serviceOrder.manpower)} crew`}
                                icon={<Users size={13} />}
                              />
                            )}
                          {data?.serviceOrder?.duration !== null &&
                            data?.serviceOrder?.duration !== undefined && (
                              <DetailField
                                label="Duration"
                                value={`${String(data.serviceOrder.duration)} hrs`}
                                icon={<Timer size={13} />}
                              />
                            )}
                          {data?.serviceOrder?.defaultCrew !== null &&
                            data?.serviceOrder?.defaultCrew !== undefined &&
                            data.serviceOrder.defaultCrew !== '' && (
                              <DetailField
                                label="Default Crew"
                                value={data.serviceOrder.defaultCrew}
                                icon={<Layers size={13} />}
                              />
                            )}
                        </div>
                        {displayOrder.description !== '' && (
                          <div>
                            <p className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">
                              Description
                            </p>
                            <p className="text-sm text-text leading-relaxed">
                              {displayOrder.description}
                            </p>
                          </div>
                        )}
                        {data?.serviceOrder?.checklistTemplate !== null &&
                          data?.serviceOrder?.checklistTemplate !== undefined && (
                            <div>
                              <p className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">
                                Checklist Template
                              </p>
                              <div className="flex items-center gap-2">
                                <ClipboardList size={14} className="text-primary" />
                                <span className="text-sm text-text font-medium">
                                  {data.serviceOrder.checklistTemplate.name}
                                </span>
                                {data.serviceOrder.checklistTemplate.jobType !== null && (
                                  <GlassBadge variant="info">
                                    {data.serviceOrder.checklistTemplate.jobType}
                                  </GlassBadge>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>

            {/* Row 2: Activity + Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Activity Timeline */}
              {!isNew && <ActivityTimeline serviceOrderAk={accessKey} />}

              {/* Unit Status */}
              {!isNew && <StatusWidget serviceOrderAccessKey={accessKey} />}
            </div>
          </div>
        </GlassTabPanel>
      )}

      {/* Units tab */}
      {activeTab === 'units' && <UnitsTab serviceOrderAccessKey={accessKey} />}

      {/* Performance tab */}
      {activeTab === 'performance' && !isNew && (
        <GlassTabPanel>
          <ServiceOrderPerformance serviceOrderAk={accessKey} />
        </GlassTabPanel>
      )}

      {/* Delete confirmation */}
      <GlassConfirmDialog
        open={showDelete}
        title="Delete Service Order"
        message={`Are you sure you want to delete order "${displayOrder.orderNumber}"? This action cannot be undone.`}
        confirmLabel="Delete Order"
        variant="danger"
        loading={deleting}
        onCancel={() => {
          setShowDelete(false);
        }}
        onConfirm={() => {
          void deleteServiceOrder({ variables: { accessKey } }).then(() => {
            void navigate('/service-orders');
          });
        }}
      />
    </div>
  );
}
